import React, { useState, useEffect } from 'react';
import { ALL_RINGS, RING_RARITIES } from '../services/profileRingService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

function ProfileRing({ 
  children, 
  ringId = null, 
  ringData: propRingData = null,
  size = 'medium', 
  className = '', 
  onClick = null 
}) {
  const [ringData, setRingData] = useState(propRingData);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If ringData is provided as prop, use it directly
    if (propRingData) {
      setRingData(propRingData);
      return;
    }

    const loadRingData = async () => {
      if (!ringId) {
        setRingData(null);
        return;
      }

      // First check if it's a default ring
      if (ALL_RINGS[ringId]) {
        setRingData(ALL_RINGS[ringId]);
        return;
      }

      // If not found in default rings, check custom rings
      setLoading(true);
      try {
        // Check custom purchaseable rings
        const customRingRef = doc(db, 'customPurchaseableRings', ringId);
        const customRingDoc = await getDoc(customRingRef);
        
        if (customRingDoc.exists()) {
          setRingData(customRingDoc.data());
          return;
        }

        // Check custom admin rings
        const adminRingRef = doc(db, 'customRings', ringId);
        const adminRingDoc = await getDoc(adminRingRef);
        
        if (adminRingDoc.exists()) {
          setRingData(adminRingDoc.data());
          return;
        }

        // Ring not found
        setRingData(null);
      } catch (error) {
        console.error('Error loading ring data:', error);
        setRingData(null);
      } finally {
        setLoading(false);
      }
    };

    loadRingData();
  }, [ringId, propRingData]);


  const rarity = ringData ? RING_RARITIES[ringData.rarity] : null;
  
  const sizeClasses = {
    small: 'profile-ring-small',
    medium: 'profile-ring-medium', 
    large: 'profile-ring-large',
    xlarge: 'profile-ring-xlarge'
  };
  
  const baseClasses = [
    'profile-ring-container',
    sizeClasses[size] || sizeClasses.medium,
    ringData?.cssClass || '',
    className
  ].filter(Boolean).join(' ');
  
  const ringStyle = {};
  
  if (ringData && rarity) {
    ringStyle['--ring-color'] = rarity.color;
    if (rarity.glow) {
      ringStyle['--ring-glow'] = `0 0 20px ${rarity.color}40`;
    }
  }
  
  return (
    <div 
      className={baseClasses}
      style={ringStyle}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="profile-ring-inner">
        {children}
      </div>
      
      {/* Only show ring border if user has actual ring data (not default fallback) */}
      {ringData && (
        <div className="profile-ring-border" data-ring={ringData.id || 'default'}>
          {/* Custom image ring */}
          {ringData.imageUrl && (
            <div className="ring-custom-image">
              <img 
                src={ringData.imageUrl} 
                alt={`${ringData.name} ring`}
                className="ring-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Failed to load ring image:', ringData.imageUrl);
                }}
              />
            </div>
          )}
          
          {ringData.rarity === 'legendary' && (
            <div className="ring-sparkles">
              <span className="sparkle sparkle-1">✨</span>
              <span className="sparkle sparkle-2">✨</span>
              <span className="sparkle sparkle-3">✨</span>
            </div>
          )}
          
          {ringData.rarity === 'special' && (
            <div className="ring-special-aura"></div>
          )}
        </div>
      )}
    </div>
  );
}

export default ProfileRing;